import { DOCUMENT, DatePipe } from '@angular/common';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, computed, inject, input, output } from '@angular/core';
import { ContentListComponent, ContentListItemComponent } from '@workbuddy/ui/components/content-list';
import { Command } from '@workbuddy/ui/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { sessionListBridgeStyles, sessionListStyleId } from './session.list.theme';
import { SessionItem, SessionItemViewModel } from './session.list.model';

@Component({
  selector: 'wb-session-list',
  standalone: true,
  imports: [ContentListComponent, ContentListItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: 'session.list.component.html',
  styleUrls: ['session.list.component.less'],
})
export class SessionListComponent {

  private readonly document = inject(DOCUMENT);

  public $sessions = input<SessionItem[]>([], { alias: 'sessions' });
  public $limit = input<number>(3, { alias: 'limit' });
  public $title = input<string>('Active session limit reached', { alias: 'title' });
  public $listTitle = input<string>('Active sessions', { alias: 'listTitle' });

  public endSession = output<SessionItem>();
  public endAll = output<void>();
  public tryAgain = output<void>();

  protected $endAllCommand = new Command({ label: 'End all sessions', action: () => this.endAll.emit() });

  protected $vm = computed<SessionItemViewModel[]>(() => {

    return this.$sessions().map(session => ({
      session,
      endCommand: new Command({ label: 'End', action: () => this.endSession.emit(session) }),
    }));
  });

  constructor() {

    DesignSystem.Mount(this.document, sessionListStyleId, sessionListBridgeStyles);
  }

  protected $relativeTime(date: Date): string {

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    return 'just now';
  }

  protected $sessionHeader(session: SessionItem): string {

    const browser = this.$browserName(session.browser);
    const os = session.os?.trim();

    if (browser && os) {
      return `${browser} on ${os}`;
    }

    return browser || os || (session.startedAt ? new DatePipe('en-AU').transform(session.startedAt, 'MMMM d, yyyy') : 'Unknown');
  }

  protected $sessionDescription(session: SessionItem): string {

    const lastSeen = session.lastSeenAt ?? session.startedAt;

    return `Last seen ${this.$relativeTime(lastSeen)}`;
  }

  private $browserName(browser?: string): string {

    switch ((browser ?? '').trim().toLowerCase()) {
      case 'ms-edge':
      case 'edge':
        return 'Edge';
      default:
        return browser || '';
    }
  }
}
